import type {
  ExtendedResource,
  ExtendedResourceConstructor,
} from '@seen/core/ExtendedResource'
import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export interface UseDeleteMutationOptions<T> {
  mutation?: Omit<UseMutationOptions<T, unknown, T>, 'mutationFn'>
}

export function useDeleteMutation<T extends ExtendedResource>(
  resourceClass: ExtendedResourceConstructor<T>,
  options?: UseDeleteMutationOptions<T>
): UseMutationResult<T, unknown, T> {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const isReadOnly = useIsReadOnly()

  return useMutation({
    ...options?.mutation,
    onSuccess: (_, variables, context) => {
      cacheKeys.invalidateMutation(
        queryClient,
        resourceClass.RESOURCE_TYPE,
        variables.id
      )
      options?.mutation?.onSuccess?.(_, variables, context)
    },
    mutationFn: (instance) => {
      if (isReadOnly) {
        console.warn(
          '[read-only] mutation attempted: useDeleteMutation',
          resourceClass.RESOURCE_TYPE
        )
      }
      return medplum.deleteResource(resourceClass.RESOURCE_TYPE, instance.id)
    },
  })
}
